import {
    useState,
    useEffect,
    useRef,
    useCallback,
    lazy,
    Suspense,
    useContext,
} from '@wordpress/element';
import { filterTypes } from '../../config';
import LoadingSpinner from '../LoadingSpinner';
import { ConfigContext } from '../../index.js';
import Pills from './Pills.jsx';
import SpecialsToggle from './SpecialsToggle.jsx';
import { RetailerIdContext } from '../../App.jsx';
import { QueryArgsContext } from '../../views/Menu.jsx';
import { useCachedQueryData } from '../../hooks/useCachedQueryData.js';
import isEqual from 'lodash.isequal';
import { useRetailerData, useCartData } from '../../hooks/dutchie-queries.js';

const FilterModules = lazy(() => import('./FilterModules.jsx'));
const SearchBar = lazy(() => import('./SearchBar'));
const OrderType = lazy(() => import('../Cart/OrderType.jsx'));
const AddressInput = lazy(() => import('../Cart/AddressInput.jsx'));

const Filter = ({
    filterTerms = {},
    selectedFilters = {},
    setSelectedFilters,
    styleOptions,
    categoriesToShow = [],
    hideCategoryFilter,
    hideSubcategoryFilter,
    hideTagsFilter,
    specialsExist,
    showDeliveryPickup,
    forcedOrderType,
    thcMin
}) => {
    const retailerId = useContext(RetailerIdContext);
    const queryArgs = useContext(QueryArgsContext);
    const { menuFilter } = useContext(ConfigContext);
    const filterWrapperRef = useRef(null);
    const [toggleMobileDisplayAllFilters, setToggleMobileDisplayAllFilters] =
        useState(false);

    const { data: cartData, isFetched: isFetchedCartData } =
        useCartData(retailerId);
    const { data: retailerData, isFetched: isFetchedRetailerData } =
        useRetailerData(retailerId);

    const [isValidAddress, setIsValidAddress] = useState(false);

    /**
     * Flat array of categories set in Shop Editor.
     * @returns {Array}
     */
    const filterCatsToShow = () => {
        const miscCatsToExclude = ['SPECIALS', 'DEALS'];

        return categoriesToShow
            .filter(
                ({
                    headless_categories_to_show_menu_show: shouldShow,
                    headless_categories_to_show_menu_name: catName,
                }) => shouldShow && !miscCatsToExclude.includes(catName)
            )
            .map(
                ({ headless_categories_to_show_menu_name: catName }) => catName
            );
    };

    /**
     * Returns categories set up in shop editor config or default categories.
     * @returns {Array}
     */
    const initialCategories = () => {
        // categories set in Shop editor.
        if (Array.isArray(categoriesToShow) && categoriesToShow.length > 0) {
            return filterCatsToShow();
        } else if (filterTerms?.categories) {
            // all default categories.
            return filterTerms.categories;
        } else {
            // probably an error if it gets here.
            return [];
        }
    };

    /**
     * Actual filter terms to be passed to filter modules.
     */
    const [menuFilterTerms, setMenuFilterTerms] = useState({
        ...filterTerms,
        categories: initialCategories(),
    });

    /**
     * Hook into useMenuProducts to get current menu products.
     */
    const cachedProductsData = useCachedQueryData([
        retailerId,
        queryArgs?.queryName,
        queryArgs?.variables,
    ]);

    /**
     * Handle setting menuFilterTerms when productsData changes.
     * This controls which options in each filter module are visible.
     */
    const updateFilterTerms = useCallback(() => {
        if (
            filterTerms &&
            menuFilterTerms &&
            queryArgs &&
            cachedProductsData?.pages
        ) {
            // data from useMeuProducts query.
            const products = cachedProductsData.pages.flatMap(
                (page) => page.data
            );
            const brands = cachedProductsData.pages.flatMap(
                (page) => page.brands
            );

            // all filter options filtered down based on what is available in
            // cached products data.
            const categories = initialCategories().filter((cat) =>
                products.some((p) => p.category === cat)
            );
            const subcategories = filterTerms.subcategories.filter((subcat) =>
                products.some((p) => p.subcategory === subcat)
            );
            const strains = filterTerms.strains.filter((strain) =>
                products.some((p) => p.strainType === strain)
            );
            const effects = filterTerms.effects.filter((eff) =>
                products.some((p) => p.effects && p.effects.includes(eff))
            );
            const weights = filterTerms.weights.filter((weight) =>
                products.some((p) =>
                    p.variants &&
                    p.variants.some(variant => variant.option === weight)
                )
            );
            const tags = filterTerms.tags.filter((tag) =>
                products.some((p) => p.tags && p.tags.includes(tag))
            );

            // if no filters are selected, show the default terms.
            if (isEqual(selectedFilters, menuFilter)) {
                setMenuFilterTerms({
                    ...filterTerms,
                    categories: initialCategories(),
                });
            } else {
                const updatedTerms = {};

                // checks if the above terms differ from what is currently
                // displayed. If different, then update the currently visible filter
                // options with the above terms.
                if (
                    selectedFilters.category !== '' ||
                    !isEqual(categories, menuFilterTerms?.categories)
                ) {
                    updatedTerms.categories = categories;
                }
                if (!isEqual(brands, menuFilterTerms?.brands)) {
                    updatedTerms.brands = brands;
                }
                if (!isEqual(subcategories, menuFilterTerms?.subcategories)) {
                    updatedTerms.subcategories = subcategories;
                }
                if (!isEqual(strains, menuFilterTerms?.strains)) {
                    updatedTerms.strains = strains;
                }
                if (!isEqual(weights, menuFilterTerms?.weights)) {
                    updatedTerms.weights = weights;
                }
                if (!isEqual(effects, menuFilterTerms?.effects)) {
                    updatedTerms.effects = effects;
                }
                if (!isEqual(tags, menuFilterTerms?.tags)) {
                    updatedTerms.tags = tags;
                }

                delete updatedTerms.potencyThc;
                delete updatedTerms.potencyCbd;

                setMenuFilterTerms((prevState) => ({
                    ...filterTerms,
                    ...prevState,
                    categories: initialCategories(),
                    ...updatedTerms,
                }));
            }
        }
    }, [queryArgs, cachedProductsData, filterTerms]);

    /**
     * Update filter terms every time the displayed products are changed.
     */
    useEffect(() => {
        if (cachedProductsData) {
            updateFilterTerms();
        }
    }, [selectedFilters, cachedProductsData, updateFilterTerms]);



    /**
     * Handle updating selectedFilters when a filter item is toggled.
     */
    const handleChange = useCallback((target, filterType) => {
        setSelectedFilters((prevFilters) => {
            const newFilters = { ...prevFilters };

            if (
                filterType === filterTypes.category &&
                (target.value === filterTypes.staff_picks ||
                    target.value === filterTypes.specials)
            ) {
                newFilters[filterTypes.staff_picks] =
                    target.value === filterTypes.staff_picks
                        ? !prevFilters.staff_picks
                        : false;
                newFilters[filterTypes.specials] =
                    target.value === filterTypes.specials
                        ? !prevFilters.specials
                        : false;
            } else if (
                [
                    filterTypes.effects,
                    filterTypes.weights,
                    filterTypes.brands,
                    filterTypes.tags,
                ].includes(filterType)
            ) {
                if (target.value === '') {
                    newFilters[filterType] = [];
                    newFilters[filterTypes.specials] = false;
                    newFilters[filterTypes.staff_picks] = false;
                } else {
                    newFilters[filterType] = Array.isArray(
                        prevFilters[filterType]
                    )
                        ? prevFilters[filterType].includes(target.value)
                            ? prevFilters[filterType].filter(
                                (filter) => filter !== target.value
                            )
                            : [...prevFilters[filterType], target.value]
                        : [target.value];
                    newFilters[filterTypes.specials] =
                        filterType === filterTypes.brands
                            ? prevFilters.specials
                            : false;
                    newFilters[filterTypes.staff_picks] = false;
                }
            } else {
                newFilters[filterType] =
                    prevFilters[filterType] === target.value
                        ? ''
                        : target.value;
                newFilters[filterTypes.staff_picks] = false;
            }

            return newFilters;
        });
    }, []);

    // Handle updating filters fromFilterModuleRange components.
    const handleRangeChange = useCallback((updatedRange, filterType) => {
        setSelectedFilters((prevFilters) => ({
            ...prevFilters,
            [filterType]: {
                ...prevFilters[filterType],
                min: updatedRange.min ? parseFloat(updatedRange.min) : undefined,
                max: updatedRange.max ? parseFloat(updatedRange.max) : undefined,
            },
        }));
    }, [setSelectedFilters]);

    // Reset selected filters and remove overlay on mobile.
    const handleOverlayClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedFilters(menuFilter);
        setToggleMobileDisplayAllFilters(false);
        document.body.classList.remove('lock-scroll');
    }, []);

    // Toggle mobile overlay.
    useEffect(() => {
        document.body.style.overflow = toggleMobileDisplayAllFilters
            ? 'hidden'
            : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [toggleMobileDisplayAllFilters]);

    /**
     * Determine if we show pickup/delivery options.
     * Admin field must be enabled.
     * Both pickup and delivery are available options.
     */
    const [showOrderType, setShowOrderType] = useState(false);

    const hasPickupOption = (retailerData) => {
        return (
            retailerData?.fulfillmentOptions.pickup ||
            retailerData?.fulfillmentOptions.curbsidePickup ||
            retailerData?.fulfillmentOptions.driveThruPickup
        );
    };

    useEffect(() => {
        if (!isFetchedCartData || !isFetchedRetailerData) {
            return;
        }

        if (cartData && retailerData) {
            const pickupAvailable = hasPickupOption(retailerData);
            const deliveryAvailable = retailerData?.fulfillmentOptions.delivery;

            if (pickupAvailable && deliveryAvailable && showDeliveryPickup) {
                setShowOrderType(true);
            }
        }
    }, [cartData, retailerData, isFetchedCartData, isFetchedRetailerData, showDeliveryPickup]);

    return (
        <>
            {showOrderType ? (
                <div className='filter-sidebar-ordertype filter-sidebar-ordertype-mobile'>
                    <OrderType forcedOrderType={forcedOrderType} />
                    {isFetchedCartData && cartData?.orderType === 'DELIVERY' && (
                        <div className='headless-cart-sidebar-address'>
                            <AddressInput
                                setIsValidAddress={setIsValidAddress}
                            />
                        </div>
                    )}
                </div>
            ) : null}
            {toggleMobileDisplayAllFilters && (
                <div
                    onClick={handleOverlayClick}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleOverlayClick(e);
                        }
                    }}
                    role="button"
                    tabIndex="0"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        cursor: 'pointer',
                    }}
                />
            )}
            <div className="headless-mobile-filter-search">
                <SearchBar
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                />
            </div>
            {specialsExist && (
                <div className="headless-mobile-filter-specials">
                    <SpecialsToggle
                        handleChange={handleChange}
                        selectedFilters={selectedFilters}
                        deviceType="mobile"
                    />
                </div>
            )}
            <button
                className="headless-filter-toggle-all"
                onClick={() => {
                    setToggleMobileDisplayAllFilters(
                        !toggleMobileDisplayAllFilters
                    );
                    document.body.classList.toggle(
                        'lock-scroll',
                        !toggleMobileDisplayAllFilters
                    );
                }}
                aria-expanded={toggleMobileDisplayAllFilters}
                aria-controls="filter-sidebar-content"
            >
                {toggleMobileDisplayAllFilters
                    ? 'Hide Filters'
                    : 'More Filters'}
            </button>
            <div
                id="filter-sidebar-content"
                className={`headless-filter-wrapper ${toggleMobileDisplayAllFilters ? 'filter-toggled' : ''
                    }`}
                ref={filterWrapperRef}
                role="complementary"
                aria-label="Product filters"
            >
                {showOrderType ? (
                    <div className='filter-sidebar-ordertype filter-sidebar-ordertype-desktop'>
                        <OrderType forcedOrderType={forcedOrderType} />
                        {isFetchedCartData && cartData?.orderType === 'DELIVERY' && (
                            <div className='headless-cart-sidebar-address'>
                                <AddressInput
                                    setIsValidAddress={setIsValidAddress}
                                />
                            </div>
                        )}
                    </div>
                ) : null}

                <button
                    className='mobile-filter-close-x'
                    onClick={() => {
                        setToggleMobileDisplayAllFilters(
                            !toggleMobileDisplayAllFilters
                        );
                        document.body.classList.toggle(
                            'lock-scroll',
                            !toggleMobileDisplayAllFilters
                        );
                    }}
                    aria-label="Close filters"
                >
                    <div className='mobile-filter-close-x-icon' aria-hidden='true'>
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 384 512'><path d='M376.6 84.5c11.3-13.6 9.5-33.8-4.1-45.1s-33.8-9.5-45.1 4.1L192 206 56.6 43.5C45.3 29.9 25.1 28.1 11.5 39.4S-3.9 70.9 7.4 84.5L150.3 256 7.4 427.5c-11.3 13.6-9.5 33.8 4.1 45.1s33.8 9.5 45.1-4.1L192 306 327.4 468.5c11.3 13.6 31.5 15.4 45.1 4.1s15.4-31.5 4.1-45.1L233.7 256 376.6 84.5z' /></svg>
                    </div>
                </button>

                <div className="headless-filter-wrapper-inner">
                    <div className="headless-desktop-filter-search">
                        <SearchBar
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                        />
                    </div>

                    <Pills
                        selectedFilters={selectedFilters}
                        handleChange={handleChange}
                        filterTerms={filterTerms}
                    />

                    <Suspense fallback={<LoadingSpinner />}>
                        <FilterModules
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            filterTerms={filterTerms}
                            handleChange={handleChange}
                            handleRangeChange={handleRangeChange}
                            styleOptions={styleOptions}
                            categoriesToShow={categoriesToShow}
                            mobileDisplay={{
                                toggleMobileDisplayAllFilters,
                                setToggleMobileDisplayAllFilters,
                            }}
                            specialsExist={specialsExist}
                            menuFilterTerms={menuFilterTerms}
                            hideFilters={{
                                cat: hideCategoryFilter,
                                subcat: hideSubcategoryFilter,
                                tags: hideTagsFilter,
                            }}
                            thcMin={thcMin}
                        />
                    </Suspense>
                </div>
            </div>
        </>
    );
};

export default Filter;
